import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Users, ArrowLeft, UserPlus, LogOut, Trash2, MessageCircle, CheckCircle, X, Trophy } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getAvatarUrl } from '../utils/avatarUrl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MatchDetailPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [submittingResult, setSubmittingResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [sets, setSets] = useState([{ p1: '', p2: '' }, { p1: '', p2: '' }]);
  const [showThirdSet, setShowThirdSet] = useState(false);

  useEffect(() => {
    fetchMatch();
  }, [matchId]);

  const fetchMatch = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/matches/${matchId}`, {
        withCredentials: true
      });
      setMatch(response.data);
    } catch (error) {
      console.error('Error fetching match:', error);
      toast.error('Partita non trovata');
      navigate('/matches');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/join`, {}, {
        withCredentials: true
      });
      toast.success('Ti sei iscritto alla partita!');
      fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/leave`, {}, {
        withCredentials: true
      });
      toast.success('Hai lasciato la partita');
      fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/api/matches/${matchId}`, {
        withCredentials: true
      });
      toast.success('Partita cancellata');
      navigate('/matches');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/complete`, {}, {
        withCredentials: true
      });
      toast.success('Partita completata!');
      fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/decline`, {}, {
        withCredentials: true
      });
      toast.success('Invito rifiutato');
      fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setActionLoading(false);
    }
  };

  const submitResult = async () => {
    if (!selectedWinner) return;
    
    // Build sets array (only non-empty)
    const validSets = [];
    const allSets = showThirdSet ? sets.concat([sets[2] || { p1: '', p2: '' }]).slice(0, 3) : sets.slice(0, 2);
    for (const s of allSets) {
      const p1 = parseInt(s.p1);
      const p2 = parseInt(s.p2);
      if (!isNaN(p1) && !isNaN(p2)) {
        validSets.push({ p1, p2 });
      }
    }
    
    setSubmittingResult(true);
    try {
      const res = await axios.post(`${API_URL}/api/matches/${matchId}/result`, 
        { winner_id: selectedWinner, sets: validSets },
        { withCredentials: true }
      );
      setResultData(res.data);
      toast.success('Risultato registrato!');
      setSelectedWinner(null);
      fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel registrare il risultato');
    } finally {
      setSubmittingResult(false);
    }
  };

  const updateSet = (index, player, value) => {
    const val = value.replace(/[^0-9]/g, '');
    if (val.length > 2) return;
    setSets(prev => {
      const updated = [...prev];
      while (updated.length <= index) updated.push({ p1: '', p2: '' });
      updated[index] = { ...updated[index], [player]: val };
      return updated;
    });
  };

  const getScoreDisplay = (matchSets, player1Id, player2Id) => {
    if (!matchSets || matchSets.length === 0) return null;
    return matchSets.map((s, i) => {
      const isP1Winner = player1Id === match?.result?.winner_id;
      return (
        <span key={i} className="inline-flex items-center gap-0.5 text-sm font-mono">
          <span className={isP1Winner && s.p1 > s.p2 ? 'font-bold' : 'text-slate-500'}>{s.p1}</span>
          <span className="text-slate-300">-</span>
          <span className={!isP1Winner && s.p2 > s.p1 ? 'font-bold' : 'text-slate-500'}>{s.p2}</span>
        </span>
      );
    });
  };

  const formatMatchDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEEE d MMMM yyyy', { locale: it });
    } catch {
      return dateStr;
    }
  };

  const isParticipant = match?.players.some(p => p.user_id === user?.user_id);
  const isCreator = match?.creator_id === user?.user_id;
  const canJoin = match?.status === 'open' && !isParticipant;
  const myInvite = match?.invited_players?.find(p => p.user_id === user?.user_id);
  const isInvited = myInvite?.status === 'pending';

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-64 bg-slate-100 rounded-3xl animate-pulse" />
      </div>
    );
  }

  if (!match) return null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="match-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-outfit text-xl font-semibold text-slate-900">
            Dettaglio Partita
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {match.match_type === 'classifica' && (
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="match-type-badge">
              Classifica
            </span>
          )}
          <span className={`text-sm font-medium px-3 py-1 rounded-full ${
            match.status === 'open'
              ? 'bg-green-100 text-green-600'
              : match.status === 'full'
              ? 'bg-sky-100 text-sky-600'
              : match.status === 'completed'
              ? 'bg-emerald-100 text-emerald-600'
              : 'bg-slate-100 text-slate-600'
          }`}>
            {match.status === 'open' ? 'Aperta' : match.status === 'full' ? 'Completa' : match.status === 'completed' ? 'Giocata' : match.status}
          </span>
        </div>
      </div>

      {/* Match Info Card */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 mb-6">
        {/* Date & Time */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
          <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center">
            <Calendar className="w-7 h-7 text-sky-500" />
          </div>
          <div>
            <p className="text-slate-500 text-sm">Data e Ora</p>
            <p className="font-outfit text-lg font-semibold text-slate-900 capitalize">
              {formatMatchDate(match.date)}
            </p>
            <p className="text-slate-700 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {match.time}
            </p>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
            <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <p className="text-slate-500 text-sm">Luogo</p>
            <p className="font-outfit text-lg font-semibold text-slate-900">
              Ananda Sport
            </p>
            <p className="text-slate-600">Campo del circolo</p>
          </div>
        </div>

        {/* Organizer */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
            <svg className="w-7 h-7 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-slate-500 text-sm">Organizzata da</p>
            <p className="font-outfit text-lg font-semibold text-slate-900">
              {match.creator_name}
            </p>
          </div>
          {!isCreator && (
            <Link
              to={`/chat?user=${match.creator_id}`}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-100 text-sky-500 hover:bg-sky-200 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
            </Link>
          )}
        </div>
      </div>

      {/* Players Card */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-outfit text-lg font-semibold text-slate-900">
            Giocatori
          </h2>
          <span className="text-sm text-slate-500">
            {match.players.length}/{match.max_players}
          </span>
        </div>

        <div className="space-y-3">
          {match.players.map((player) => (
            <div
              key={player.user_id}
              className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50"
            >
              <div className="w-10 h-10 bg-sky-400 rounded-full flex items-center justify-center text-white font-medium overflow-hidden">
                {getAvatarUrl(player) ? (
                  <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  player.name?.charAt(0)
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{player.name}</p>
                {player.user_id === match.creator_id && (
                  <p className="text-xs text-sky-500">Organizzatore</p>
                )}
              </div>
              {player.user_id !== user?.user_id && (
                <Link
                  to={`/players/${player.user_id}`}
                  className="text-sm text-sky-500 hover:text-sky-600"
                >
                  Profilo
                </Link>
              )}
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: match.max_players - match.players.length }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="flex items-center gap-3 p-3 rounded-2xl border-2 border-dashed border-slate-200"
            >
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-slate-400">Posto disponibile</p>
            </div>
          ))}
        </div>
      </div>

      {/* Invited Players Section */}
      {match.invited_players && match.invited_players.length > 0 && (
        <div className="bg-white rounded-3xl p-6 border border-slate-100 mb-6" data-testid="invited-players-section">
          <h2 className="font-outfit text-lg font-semibold text-slate-900 mb-4">
            Giocatori Invitati
          </h2>
          <div className="space-y-3">
            {match.invited_players.map((inv) => (
              <div
                key={inv.user_id}
                className="flex items-center gap-3 p-3 rounded-2xl bg-amber-50/50 border border-amber-100"
              >
                <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center text-white font-medium">
                  {inv.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{inv.name}</p>
                  <p className={`text-xs ${
                    inv.status === 'accepted' ? 'text-green-500' :
                    inv.status === 'declined' ? 'text-red-500' :
                    'text-amber-500'
                  }`}>
                    {inv.status === 'accepted' ? 'Accettato' :
                     inv.status === 'declined' ? 'Rifiutato' :
                     'In attesa di risposta'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Response (for invited user) */}
      {isInvited && !isParticipant && match.status === 'open' && (
        <div className="bg-amber-50 rounded-3xl p-6 border border-amber-200 mb-6" data-testid="invite-response-section">
          <p className="text-center font-medium text-amber-800 mb-4">
            Sei stato invitato a questa partita!
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleJoin}
              disabled={actionLoading}
              data-testid="accept-invite-btn"
              className="flex-1 py-3 bg-green-500 text-white rounded-full font-medium hover:bg-green-600 transition-all flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Accetta
            </button>
            <button
              onClick={handleDecline}
              disabled={actionLoading}
              data-testid="decline-invite-btn"
              className="flex-1 py-3 bg-white text-red-500 border border-red-200 rounded-full font-medium hover:bg-red-50 transition-all flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              Rifiuta
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        {canJoin && (
          <button
            onClick={handleJoin}
            disabled={actionLoading}
            data-testid="join-match-btn"
            className="w-full py-4 bg-sky-400 text-white rounded-full font-medium text-lg hover:bg-sky-500 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {actionLoading ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Partecipa
              </>
            )}
          </button>
        )}

        {isParticipant && !isCreator && match.status !== 'completed' && (
          <button
            onClick={handleLeave}
            disabled={actionLoading}
            data-testid="leave-match-btn"
            className="w-full py-4 bg-white text-slate-700 border border-slate-200 rounded-full font-medium hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Lascia partita
          </button>
        )}

        {isCreator && match.status === 'full' && (
          <button
            onClick={handleComplete}
            disabled={actionLoading}
            data-testid="complete-match-btn"
            className="w-full py-4 bg-green-500 text-white rounded-full font-medium hover:bg-green-600 transition-all flex items-center justify-center gap-2"
          >
            {actionLoading ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Segna come completata
              </>
            )}
          </button>
        )}

        {/* Result submission - for participants of completed matches without result */}
        {match.status === 'completed' && !match.result && isParticipant && match.players.length >= 2 && (
          <div className="bg-amber-50 rounded-3xl p-6 border border-amber-200" data-testid="submit-result-section">
            {!selectedWinner ? (
              <>
                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2 text-center">
                  Chi ha vinto?
                </h3>
                <p className="text-sm text-slate-500 text-center mb-4">
                  {match.match_type === 'classifica' ? 'Il risultato aggiornerà automaticamente il ranking.' : 'Registra il risultato della partita.'}
                </p>
                <div className="space-y-2">
                  {match.players.map((player) => (
                    <button
                      key={player.user_id}
                      onClick={() => setSelectedWinner(player.user_id)}
                      data-testid={`select-winner-${player.user_id}`}
                      className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border-2 border-slate-100 hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                    >
                      <div className="w-10 h-10 bg-sky-400 rounded-full flex items-center justify-center text-white font-medium overflow-hidden">
                        {getAvatarUrl(player) ? (
                          <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-full object-cover" />
                        ) : player.name?.charAt(0)}
                      </div>
                      <span className="font-medium text-slate-900 flex-1 text-left">{player.name}</span>
                      <Trophy className="w-5 h-5 text-amber-400" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-1 text-center">
                  Inserisci il Punteggio
                </h3>
                <p className="text-sm text-slate-500 text-center mb-4">
                  Vincitore: <span className="font-semibold text-emerald-600">{match.players.find(p => p.user_id === selectedWinner)?.name}</span>
                </p>

                {/* Player names header */}
                <div className="flex items-center gap-2 mb-3 px-2">
                  <div className="w-16" />
                  {match.players.map(p => (
                    <div key={p.user_id} className="flex-1 text-center">
                      <span className={`text-xs font-semibold ${p.user_id === selectedWinner ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {p.name?.split(' ')[0]}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Set inputs */}
                {[0, 1].map(setIdx => (
                  <div key={setIdx} className="flex items-center gap-2 mb-2">
                    <span className="w-16 text-sm font-medium text-slate-500">Set {setIdx + 1}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={sets[setIdx]?.p1 || ''}
                      onChange={e => updateSet(setIdx, 'p1', e.target.value)}
                      data-testid={`set-${setIdx + 1}-p1`}
                      className="flex-1 h-12 text-center text-xl font-bold bg-white border-2 border-slate-200 rounded-xl focus:border-sky-400 focus:outline-none transition-colors"
                      placeholder="0"
                    />
                    <span className="text-slate-300 font-bold">-</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={sets[setIdx]?.p2 || ''}
                      onChange={e => updateSet(setIdx, 'p2', e.target.value)}
                      data-testid={`set-${setIdx + 1}-p2`}
                      className="flex-1 h-12 text-center text-xl font-bold bg-white border-2 border-slate-200 rounded-xl focus:border-sky-400 focus:outline-none transition-colors"
                      placeholder="0"
                    />
                  </div>
                ))}

                {/* Third set toggle */}
                {!showThirdSet ? (
                  <button
                    onClick={() => { setShowThirdSet(true); setSets(prev => [...prev.slice(0, 2), { p1: '', p2: '' }]); }}
                    data-testid="add-third-set"
                    className="w-full py-2 text-sm text-sky-500 font-medium hover:text-sky-600 transition-colors"
                  >
                    + Aggiungi 3° Set (Tiebreak 10 punti)
                  </button>
                ) : (
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-500 flex items-center gap-1">
                        3° Set <span className="text-xs text-amber-500">(TB 10)</span>
                      </span>
                      <button onClick={() => { setShowThirdSet(false); setSets(prev => prev.slice(0, 2)); }}
                        className="text-xs text-red-400 hover:text-red-500">Rimuovi</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16" />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={sets[2]?.p1 || ''}
                        onChange={e => updateSet(2, 'p1', e.target.value)}
                        data-testid="set-3-p1"
                        className="flex-1 h-12 text-center text-xl font-bold bg-amber-50 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors"
                        placeholder="0"
                      />
                      <span className="text-slate-300 font-bold">-</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={sets[2]?.p2 || ''}
                        onChange={e => updateSet(2, 'p2', e.target.value)}
                        data-testid="set-3-p2"
                        className="flex-1 h-12 text-center text-xl font-bold bg-amber-50 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors"
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => { setSelectedWinner(null); setSets([{ p1: '', p2: '' }, { p1: '', p2: '' }]); setShowThirdSet(false); }}
                    className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-2xl font-medium hover:bg-slate-200 transition-colors"
                    data-testid="cancel-result"
                  >
                    Indietro
                  </button>
                  <button
                    onClick={submitResult}
                    disabled={submittingResult}
                    data-testid="confirm-result"
                    className="flex-1 py-3 bg-emerald-500 text-white rounded-2xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submittingResult ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Conferma
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Result display */}
        {match.result && (
          <div className="bg-white rounded-3xl p-6 border border-slate-100" data-testid="match-result">
            <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4 text-center">Risultato</h3>
            
            {/* Score display */}
            {match.result.sets && match.result.sets.length > 0 && (
              <div className="bg-slate-50 rounded-2xl p-4 mb-4" data-testid="match-score">
                <div className="flex items-center justify-center gap-6">
                  {match.players.slice(0, 2).map((player, pIdx) => {
                    const isWinner = player.user_id === match.result.winner_id;
                    return (
                      <div key={player.user_id} className="text-center">
                        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-white font-medium overflow-hidden ${isWinner ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-slate-400'}`}>
                          {getAvatarUrl(player) ? (
                            <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-full object-cover" />
                          ) : player.name?.charAt(0)}
                        </div>
                        <p className={`text-xs mt-1.5 font-medium truncate max-w-[80px] ${isWinner ? 'text-emerald-700' : 'text-slate-500'}`}>
                          {player.name?.split(' ')[0]}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-4 mt-3">
                  {match.result.sets.map((s, i) => (
                    <div key={i} className={`px-4 py-2 rounded-xl text-center ${i === 2 ? 'bg-amber-100' : 'bg-white'} border border-slate-200`}>
                      <p className="text-[10px] text-slate-400 font-medium mb-0.5">
                        {i === 2 ? 'TB' : `Set ${i + 1}`}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-lg font-bold font-mono ${s.p1 > s.p2 ? 'text-slate-900' : 'text-slate-400'}`}>{s.p1}</span>
                        <span className="text-slate-300 text-sm">-</span>
                        <span className={`text-lg font-bold font-mono ${s.p2 > s.p1 ? 'text-slate-900' : 'text-slate-400'}`}>{s.p2}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {match.players.map((player) => {
                const isWinner = player.user_id === match.result.winner_id;
                const pointsChange = match.result.points_changes?.find(pc => pc.user_id === player.user_id);
                return (
                  <div key={player.user_id} className={`flex items-center gap-3 p-4 rounded-2xl ${isWinner ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-100'}`}>
                    <div className="w-10 h-10 bg-sky-400 rounded-full flex items-center justify-center text-white font-medium overflow-hidden">
                      {getAvatarUrl(player) ? (
                        <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-full object-cover" />
                      ) : player.name?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{player.name}</p>
                      {isWinner && <p className="text-xs text-emerald-600 font-semibold">Vincitore</p>}
                    </div>
                    {isWinner && <Trophy className="w-6 h-6 text-amber-400" />}
                    {pointsChange && (
                      <div className="text-right">
                        <span className={`text-sm font-bold ${pointsChange.total_change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {pointsChange.total_change >= 0 ? '+' : ''}{pointsChange.total_change} pts
                        </span>
                        {pointsChange.loyalty_bonus > 0 && (
                          <p className="text-xs text-amber-500">+{pointsChange.loyalty_bonus} fedeltà</p>
                        )}
                        {pointsChange.undefeated_bonus > 0 && (
                          <p className="text-xs text-emerald-500">+{pointsChange.undefeated_bonus} imbattibilità</p>
                        )}
                        {pointsChange.fascia_before !== pointsChange.fascia_after && (
                          <p className="text-xs text-purple-500 font-semibold">
                            Fascia {pointsChange.fascia_before} &#8594; {pointsChange.fascia_after}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {match.status === 'completed' && match.result && (
          <div className="w-full py-4 bg-green-100 text-green-700 rounded-full font-medium text-center flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Partita completata
          </div>
        )}

        {match.status === 'completed' && !match.result && !isParticipant && (
          <div className="w-full py-4 bg-green-100 text-green-700 rounded-full font-medium text-center flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Partita completata — risultato in attesa
          </div>
        )}

        {isCreator && match.status !== 'completed' && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="cancel-match-btn"
                className="w-full py-4 bg-red-50 text-red-600 border border-red-200 rounded-full font-medium hover:bg-red-100 transition-all flex items-center justify-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Cancella partita
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
                <AlertDialogDescription>
                  Questa azione non può essere annullata. La partita sarà cancellata definitivamente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCancel}
                  className="rounded-full bg-red-500 hover:bg-red-600"
                >
                  Cancella partita
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};

export default MatchDetailPage;
